(() => {
  'use strict';

/* Cursor-following radial light for featured and catalog product information panels. */
(() => {
  const panelSelector = [
    '.featured-collection-wrap .mm-featured-products__grid > .mm-product-card .mm-product-card__content',
    '.template-collection .collection-product-grid > .mm-product-card .mm-product-card__content'
  ].join(', ');
  const finePointer = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  let frame = 0;
  let pending = null;

  const paint = () => {
    frame = 0;
    if (!pending) return;

    const { panel, clientX, clientY } = pending;
    pending = null;
    if (!panel.isConnected) return;

    const bounds = panel.getBoundingClientRect();
    panel.style.setProperty('--mm-card-glow-x', (clientX - bounds.left) + 'px');
    panel.style.setProperty('--mm-card-glow-y', (clientY - bounds.top) + 'px');
  };

  document.addEventListener('pointermove', (event) => {
    if (!finePointer.matches || (event.pointerType && event.pointerType !== 'mouse')) return;

    const target = event.target;
    const panel = target instanceof Element ? target.closest(panelSelector) : null;
    if (!panel) return;

    pending = {
      panel,
      clientX: event.clientX,
      clientY: event.clientY
    };

    if (!frame) frame = requestAnimationFrame(paint);
  }, { passive: true });
})();


/* Catalog filter popovers stay mutually exclusive to prevent overlap. */
(() => {
  const summarySelector = '.template-collection .mm-facets__groups .mm-facet > summary';

  document.addEventListener('click', (event) => {
    const target = event.target;
    const summary = target instanceof Element ? target.closest(summarySelector) : null;
    if (!summary) return;

    const current = summary.parentElement;
    if (!(current instanceof HTMLDetailsElement) || current.open) return;

    const group = current.closest('.mm-facets__groups');
    if (!group) return;

    group.querySelectorAll('details.mm-facet[open]').forEach((facet) => {
      if (facet !== current) facet.removeAttribute('open');
    });
  }, true);
})();


  const readThemeConfig = () => {
    let embedded = {};
    try {
      embedded = JSON.parse(document.querySelector('[data-theme-config]')?.textContent || '{}');
    } catch (_) {
      embedded = {};
    }
    return embedded;
  };

  const config = readThemeConfig();
  const routes = config.routes || {};
  const moneyFormat = config.moneyFormat || '$' + '{{amount}}';
  const controllerRecords = new Set();
  const controllerKeys = new WeakMap();
  const overlayInstances = new WeakMap();
  const cartInstances = new WeakMap();
  const menuInstances = new WeakMap();
  const overlayStack = [];
  let toastTimer = null;
  let announcementId = 0;
  let revealObserver = null;

  const queryAll = (scope, selector) => {
    const nodes = [];
    if (scope instanceof Element && scope.matches(selector)) nodes.push(scope);
    if (scope && typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll(selector).forEach((node) => nodes.push(node));
    }
    return [...new Set(nodes)];
  };

  const listen = (target, type, handler, options, signal) => {
    if (!target || typeof target.addEventListener !== 'function') return;
    const normalized = typeof options === 'boolean'
      ? { capture: options, signal }
      : Object.assign({}, options || {}, { signal });
    try {
      target.addEventListener(type, handler, normalized);
    } catch (_) {
      const fallback = typeof options === 'boolean' ? options : options || false;
      target.addEventListener(type, handler, fallback);
      signal?.addEventListener('abort', () => target.removeEventListener(type, handler, fallback), { once: true });
    }
  };

  const listenToMediaQuery = (query, handler, signal) => {
    if (!query) return;
    if (typeof query.addEventListener === 'function') {
      listen(query, 'change', handler, false, signal);
      return;
    }
    if (typeof query.addListener === 'function') {
      query.addListener(handler);
      signal?.addEventListener('abort', () => query.removeListener(handler), { once: true });
    }
  };

  const mountController = (element, key, factory) => {
    if (!element) return null;
    let keys = controllerKeys.get(element);
    if (!keys) {
      keys = new Map();
      controllerKeys.set(element, keys);
    }
    if (keys.has(key)) return keys.get(key).value;

    const record = { element, key, value: null, destroy: null };
    keys.set(key, record);
    try {
      const value = factory(element) || {};
      record.value = value;
      record.destroy = typeof value.destroy === 'function' ? value.destroy : () => {};
      controllerRecords.add(record);
      return value;
    } catch (error) {
      keys.delete(key);
      console.error('DailySeasons component failed to initialize.', error);
      return null;
    }
  };

  const destroyRecord = (record) => {
    if (!controllerRecords.has(record)) return;
    controllerRecords.delete(record);
    const keys = controllerKeys.get(record.element);
    keys?.delete(record.key);
    try {
      record.destroy?.();
    } catch (error) {
      console.error('DailySeasons component failed to clean up.', error);
    }
  };

  const destroyWithin = (scope) => {
    [...controllerRecords].forEach((record) => {
      if (scope === record.element || scope?.contains?.(record.element)) destroyRecord(record);
    });
  };

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (number == null || Number.isNaN(Number(number))) return '0';
    const fixed = (Number(number) / 100).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
  };

  const formatMoney = (cents, format = moneyFormat) => {
    const value = typeof cents === 'string' ? Number(cents.replace(/[^\d-]/g, '')) : Number(cents);
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return '$' + formatWithDelimiters(value);
    let amount;
    switch (placeholder[1]) {
      case 'amount_no_decimals':
        amount = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(value, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        amount = formatWithDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = formatWithDelimiters(value, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        amount = formatWithDelimiters(value, 2, ' ', '.');
        break;
      default:
        amount = formatWithDelimiters(value, 2);
    }
    return format.replace(placeholder[0], amount);
  };

  const moneyText = (cents) => {
    const template = document.createElement('template');
    template.innerHTML = formatMoney(cents);
    return template.content.textContent || '';
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  }[character]));

  const showToast = (message) => {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add('show');
    toastTimer = window.setTimeout(() => toast.classList.remove('show'), 2800);
  };

  const announce = (status, message) => {
    if (!status) return;
    const id = String(++announcementId);
    status.dataset.announcementId = id;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      if (status.dataset.announcementId === id) status.textContent = message;
    });
  };

  const ensureStatus = (parent, selector, attributeName) => {
    if (!parent) return { node: null, created: false };
    let node = parent.querySelector(selector);
    let created = false;
    if (!node) {
      node = document.createElement('p');
      node.className = 'visually-hidden';
      node.setAttribute(attributeName, '');
      parent.appendChild(node);
      created = true;
    }
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.setAttribute('aria-atomic', 'true');
    return { node, created };
  };

  const syncBodyLock = () => {
    document.body.classList.toggle('is-locked', overlayStack.length > 0);
  };

  const isVisibleFocusTarget = (element) => {
    if (!(element instanceof HTMLElement) || !element.isConnected) return false;
    if (('disabled' in element && element.disabled) || element.getAttribute('aria-disabled') === 'true') return false;
    if (element.closest('[hidden], [inert]')) return false;
    const style = window.getComputedStyle(element);
    return style.visibility !== 'hidden' && style.display !== 'none' && element.getClientRects().length > 0;
  };

  const focusableElements = (root) => {
    if (!root) return [];
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'summary',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(',');
    return [...root.querySelectorAll(selector)].filter(isVisibleFocusTarget);
  };

  const createOverlayController = ({
    root,
    dialog,
    openSelector,
    closeSelector,
    initialFocusSelector
  }) => {
    let opener = null;
    let destroyed = false;
    const appliedInert = new Set();

    const setTriggerState = (expanded) => {
      document.querySelectorAll(openSelector).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    };

    const applyOutsideInert = () => {
      let current = root;
      while (current && current !== document.body) {
        const parent = current.parentElement;
        if (!parent) break;
        [...parent.children].forEach((sibling) => {
          if (sibling === current || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) return;
          if (!sibling.hasAttribute('inert')) {
            sibling.setAttribute('inert', '');
            appliedInert.add(sibling);
          }
        });
        current = parent;
      }
    };

    const clearOutsideInert = () => {
      appliedInert.forEach((element) => element.removeAttribute('inert'));
      appliedInert.clear();
    };

    const removeFromStack = () => {
      const index = overlayStack.indexOf(api);
      if (index !== -1) overlayStack.splice(index, 1);
    };

    const open = (trigger) => {
      if (destroyed) return;
      if (trigger instanceof HTMLElement) opener = trigger;
      if (!root.hidden) {
        removeFromStack();
        overlayStack.push(api);
        return;
      }
      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      setTriggerState(true);
      applyOutsideInert();
      removeFromStack();
      overlayStack.push(api);
      syncBodyLock();
      window.requestAnimationFrame(() => {
        if (destroyed || root.hidden) return;
        const initial = root.querySelector(initialFocusSelector) || focusableElements(dialog)[0] || dialog;
        initial?.focus?.({ preventScroll: true });
      });
    };

    const close = ({ restoreFocus = true } = {}) => {
      if (destroyed || root.hidden) return;
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      setTriggerState(false);
      clearOutsideInert();
      removeFromStack();
      syncBodyLock();
      if (restoreFocus) {
        const visibleTrigger = [...document.querySelectorAll(openSelector)].find(isVisibleFocusTarget);
        const brand = [...document.querySelectorAll(
          '[data-site-header] .brand[href], .site-header .brand[href], header .brand[href]'
        )].find(isVisibleFocusTarget);
        const target = isVisibleFocusTarget(opener) ? opener : visibleTrigger || brand;
        window.requestAnimationFrame(() => {
          if (isVisibleFocusTarget(target)) {
            target.focus({ preventScroll: true });
            return;
          }
          const temporaryTabIndex = !document.body.hasAttribute('tabindex');
          if (temporaryTabIndex) document.body.setAttribute('tabindex', '-1');
          document.body.focus({ preventScroll: true });
          if (temporaryTabIndex) document.body.removeAttribute('tabindex');
        });
      }
      opener = null;
    };

    const handleKeydown = (event) => {
      if (root.hidden || overlayStack[overlayStack.length - 1] !== api) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements(dialog);
      if (!focusable.length) {
        event.preventDefault();
        dialog?.focus?.();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const destroy = () => {
      if (destroyed) return;
      if (!root.hidden) close({ restoreFocus: false });
      destroyed = true;
      clearOutsideInert();
      removeFromStack();
      syncBodyLock();
    };

    const api = { root, dialog, closeSelector, open, close, handleKeydown, destroy };
    overlayInstances.set(root, api);
    return api;
  };

  const appendImageWidth = (source, width) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return source + (source.includes('?') ? '&' : '?') + 'width=' + width;
    }
  };

  const normalizedJsonRoute = (route) => /\.js(?:$|\?)/.test(route) ? route : route + '.js';

  const createCartController = (backdrop) => {
    const requestController = new AbortController();
    const dialog = backdrop.querySelector('[data-cart-drawer]') || backdrop;
    const content = backdrop.querySelector('[data-cart-content]');
    const statusResult = ensureStatus(dialog, '[data-cart-status]', 'data-cart-status');
    const status = statusResult.node;
    const errorStatus = backdrop.querySelector('[data-cart-error]');
    const busyIndicator = backdrop.querySelector('[data-cart-busy]');
    const overlay = createOverlayController({
      root: backdrop,
      dialog,
      openSelector: '[data-cart-open]',
      closeSelector: '[data-cart-close]',
      initialFocusSelector: '[data-cart-close], [data-overlay-initial-focus]'
    });
    let destroyed = false;
    let pendingOperations = 0;
    let operationChain = Promise.resolve();
    let disabledControls = new Map();

    const setBusy = (busy) => {
      dialog.setAttribute('aria-busy', busy ? 'true' : 'false');
      content?.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busyIndicator) busyIndicator.hidden = !busy;
      if (busy) {
        disabledControls = new Map();
        backdrop.querySelectorAll('[data-cart-change], [data-cart-remove], [data-cart-checkout], .drawer-checkout').forEach((control) => {
          disabledControls.set(control, {
            disabled: 'disabled' in control ? control.disabled : null,
            ariaDisabled: control.getAttribute('aria-disabled')
          });
          if ('disabled' in control) control.disabled = true;
          control.setAttribute('aria-disabled', 'true');
        });
      } else {
        disabledControls.forEach((previous, control) => {
          if (!control.isConnected) return;
          if (previous.disabled != null) control.disabled = previous.disabled;
          if (previous.ariaDisabled == null) control.removeAttribute('aria-disabled');
          else control.setAttribute('aria-disabled', previous.ariaDisabled);
        });
        disabledControls.clear();
      }
    };

    const clearError = () => {
      if (!errorStatus) return;
      errorStatus.hidden = true;
      errorStatus.textContent = '';
    };

    const reportError = (message) => {
      if (errorStatus) {
        errorStatus.textContent = message;
        errorStatus.hidden = false;
      }
      announce(status, message);
      showToast(message);
    };

    const runOperation = (operation) => {
      pendingOperations += 1;
      clearError();
      setBusy(true);
      const run = operationChain
        .catch(() => {})
        .then(() => {
          if (destroyed) return undefined;
          return operation();
        })
        .finally(() => {
          pendingOperations = Math.max(0, pendingOperations - 1);
          if (!pendingOperations && !destroyed) setBusy(false);
        });
      operationChain = run;
      return run;
    };

    const requestJson = async (url, options = {}) => {
      const response = await fetch(url, Object.assign({}, options, {
        signal: requestController.signal,
        headers: Object.assign({ Accept: 'application/json' }, options.headers || {})
      }));
      let result;
      try {
        result = await response.json();
      } catch (_) {
        result = {};
      }
      if (!response.ok) {
        throw new Error(result.description || result.message || 'The bag could not be updated.');
      }
      return result;
    };

    const updateCounts = (cart) => {
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = String(cart.item_count);
      });
      document.querySelectorAll('[data-cart-open]').forEach((button) => {
        button.setAttribute('aria-label', 'Bag ' + cart.item_count + ' items');
      });
    };

    const renderCart = (cart) => {
      updateCounts(cart);
      if (!content) return;
      if (!cart.item_count) {
        content.innerHTML = [
          '<div class="empty-cart">',
          '<svg class="ds-empty-bag-icon" width="56" height="56" viewBox="0 0 24 24" fill="#24553f" aria-hidden="true" focusable="false" style="display:block"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 7V6a5 5 0 0 1 10 0v1h1.2a2 2 0 0 1 2 1.8l.9 11A2 2 0 0 1 19.1 22H4.9a2 2 0 0 1-2-2.2l.9-11A2 2 0 0 1 5.8 7H7Zm2-1a3 3 0 0 1 6 0v1H9V6Zm-2 4a1 1 0 0 1 2 0v1a1 1 0 0 1-2 0v-1Zm8 0a1 1 0 0 1 2 0v1a1 1 0 0 1-2 0v-1Z"/></svg>',
          '<h3>Your bag is empty.</h3>',
          '<p>Find a little magic for your space.</p>',
          '<a class="button button-primary" data-cart-empty-action href="',
          escapeHtml(routes.allProducts || '/collections/all'),
          '">Explore the collection</a>',
          '</div>'
        ].join('');
        return;
      }

      const items = cart.items.map((item, index) => {
        const line = index + 1;
        const title = item.product_title || item.title || 'Item';
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? '<p>' + escapeHtml(item.variant_title) + '</p>'
          : '';
        const sellingPlan = item.selling_plan_allocation?.selling_plan?.name
          ? '<p>' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + '</p>'
          : '';
        const properties = Object.entries(item.properties || {})
          .filter(([name, value]) => name && !name.startsWith('_') && value != null && String(value).trim() !== '')
          .map(([name, value]) => '<p>' + escapeHtml(name) + ': ' + escapeHtml(value) + '</p>')
          .join('');
        const image = item.image
          ? '<img src="' + escapeHtml(appendImageWidth(item.image, 220)) + '" alt="" width="110" height="122" loading="lazy">'
          : '';
        return [
          '<article class="cart-item" role="listitem" data-cart-item data-line="', line,
          '" data-line-key="', escapeHtml(item.key || ''), '">',
          image,
          '<div>',
          '<a href="', escapeHtml(item.url || '#'), '"><h3>', escapeHtml(title), '</h3></a>',
          variant,
          sellingPlan,
          properties,
          '<strong data-cart-line-price>', formatMoney(item.final_line_price), '</strong>',
          '<div class="quantity">',
          '<button type="button" data-cart-change data-cart-action="decrease" data-line="', line,
          '" data-line-key="', escapeHtml(item.key || ''), '" data-quantity="', Math.max(0, item.quantity - 1),
          '" aria-label="', item.quantity <= 1 ? 'Remove ' : 'Decrease ', escapeHtml(title),
          item.quantity <= 1 ? ' from bag' : ' quantity', '">−</button>',
          '<span data-cart-line-quantity aria-live="polite">', item.quantity, '</span>',
          '<button type="button" data-cart-change data-cart-action="increase" data-line="', line,
          '" data-line-key="', escapeHtml(item.key || ''), '" data-quantity="', item.quantity + 1,
          '" aria-label="Increase ', escapeHtml(title), ' quantity">＋</button>',
          '</div>',
          '<a class="remove-link" href="', escapeHtml(item.url_to_remove || ((routes.cartChange || '/cart/change') + '?line=' + line + '&quantity=0')),
          '" data-cart-remove data-line="', line, '" data-line-key="', escapeHtml(item.key || ''), '">Remove</a>',
          '</div>',
          '</article>'
        ].join('');
      }).join('');

      let shippingMessage = '';
      const threshold = Number(config.freeShippingThreshold || 0);
      if (threshold > 0) {
        const remaining = threshold - cart.total_price;
        shippingMessage = remaining > 0
          ? '<p class="shipping-note" data-shipping-note>' + formatMoney(remaining) + ' away from FREE SHIPPING.</p>'
          : '<p class="shipping-note success" data-shipping-note>FREE SHIPPING</p>';
      }

      content.innerHTML = [
        '<div class="cart-items" role="list">', items, '</div>',
        '<div class="cart-summary">',
        '<div><span>Subtotal</span><strong data-cart-total>', formatMoney(cart.total_price), '</strong></div>',
        '<p>Shipping and taxes are calculated at checkout.</p>',
        shippingMessage,
        '<a class="button button-secondary" href="', escapeHtml(routes.cart || '/cart'),
        '">Review bag <span aria-hidden="true">↗</span></a>',
        '<form action="', escapeHtml(routes.cart || '/cart'), '" method="post">',
        '<button class="button button-primary drawer-checkout" data-cart-checkout type="submit" name="checkout">',
        'CHECKOUT</button>',
        '</form>',
        '</div>'
      ].join('');
    };

    const focusAfterChange = ({ line, key, action, removed }) => {
      window.requestAnimationFrame(() => {
        if (destroyed || backdrop.hidden) return;
        let target = null;
        if (!removed && key) {
          target = [...backdrop.querySelectorAll('[data-cart-change]')].find((button) => (
            (button.dataset.lineKey || button.dataset.key) === key && button.dataset.cartAction === action
          ));
        }
        if (!target && !removed) {
          target = backdrop.querySelector(
            '[data-cart-change][data-line="' + line + '"][data-cart-action="' + action + '"]'
          );
        }
        if (!target) {
          const remainingLine = Math.max(1, Number(line));
          target = backdrop.querySelector('[data-cart-item]:nth-child(' + remainingLine + ') [data-cart-change]')
            || backdrop.querySelector('[data-cart-item]:last-child [data-cart-change]')
            || backdrop.querySelector('[data-cart-empty-action]')
            || backdrop.querySelector('[data-cart-close]');
        }
        target?.focus?.({ preventScroll: true });
      });
    };

    const changeLine = (button) => {
      if (pendingOperations) {
        announce(status, 'Please wait for the current bag update to finish.');
        return;
      }
      const line = Number(button.dataset.line);
      const isRemove = button.matches('[data-cart-remove]');
      const quantity = isRemove ? 0 : Math.max(0, Number(button.dataset.quantity));
      if (!line || Number.isNaN(quantity)) return;
      const item = button.closest('[data-cart-item], .cart-item');
      const key = button.dataset.lineKey || button.dataset.key || item?.dataset.lineKey || '';
      const action = isRemove ? 'remove' : button.dataset.cartAction
        || (/increase/i.test(button.getAttribute('aria-label') || '') ? 'increase' : 'decrease');

      runOperation(async () => {
        try {
          const endpoint = normalizedJsonRoute(routes.cartChange || '/cart/change');
          const cart = await requestJson(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line, quantity })
          });
          renderCart(cart);
          clearError();
          announce(status, quantity === 0 ? 'Item removed from your bag.' : 'Bag quantity updated.');
          focusAfterChange({ line, key, action, removed: quantity === 0 });
        } catch (error) {
          if (error.name === 'AbortError') return;
          const message = error.message || 'The bag could not be updated.';
          reportError(message);
          window.requestAnimationFrame(() => button?.focus?.({ preventScroll: true }));
        }
      });
    };

    const shouldEnhanceForm = (form, submitter) => {
      if (!form?.matches?.('.js-product-form')) return false;
      if (submitter?.closest?.('.shopify-payment-button, shopify-accelerated-checkout')) return false;
      if (submitter?.name === 'checkout') return false;
      return true;
    };

    const addFromForm = (form, submitter) => {
      if (form.dataset.addPending === 'true') return;
      const button = submitter || form.querySelector('[data-add-to-cart], [name="add"], [type="submit"]');
      const directCheckout = Boolean(button?.matches('[data-buy-now]'));
      const checkoutError = form.querySelector('[data-checkout-error]');
      if (checkoutError) { checkoutError.hidden = true; checkoutError.textContent = ''; }
      const label = button?.querySelector('[data-add-label]');
      const originalLabel = label?.textContent;
      const originalDisabled = Boolean(button?.disabled);
      const submittedVariantId = form.querySelector('[name="id"]')?.value || '';
      // Capture enabled controls before the pending state disables quantity/variant inputs.
      const payload = new FormData(form);
      const packCount = Number(payload.get('ds_pack') || 1);
      const bundleVariants = [...form.querySelectorAll('[data-ds-pack-card].is-selected [data-ds-bundle-variant]')];
      if (packCount > 1 && bundleVariants.length === packCount) {
        const counts = new Map();
        bundleVariants.forEach(select => counts.set(select.value, (counts.get(select.value) || 0) + 1));
        payload.delete('id');
        payload.delete('quantity');
        [...counts].forEach(([id, quantity], index) => {
          payload.append('items[' + index + '][id]', id);
          payload.append('items[' + index + '][quantity]', String(quantity));
        });
      }
      payload.delete('ds_pack');
      const pendingControls = [...form.querySelectorAll(
        '[data-product-option-input], [data-product-quantity], [data-quantity-minus], [data-quantity-plus], [data-ds-pack], [data-ds-bundle-variant], [data-add-to-cart], [data-buy-now], [data-add-secondary]'
      )];
      const pendingControlStates = pendingControls.map((control) => ({
        control,
        disabled: Boolean(control.disabled)
      }));
      let failed = false;
      form.dataset.addPending = 'true';
      form.setAttribute('aria-busy', 'true');
      pendingControls.forEach((control) => {
        control.disabled = true;
      });
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      if (label) label.textContent = label.dataset.addingLabel || 'Adding…';
      runOperation(async () => {
        try {
          const result = await requestJson(normalizedJsonRoute(routes.cartAdd || '/cart/add'), {
            method: 'POST',
            body: payload
          });
          if (directCheckout) {
            window.location.assign((routes.cart || '/cart').replace(/\/cart\/?$/, '/checkout'));
            return;
          }
          const cart = await requestJson(routes.cartJson || '/cart.js');
          renderCart(cart);
          overlay.open(button);
          const message = (result.product_title || result.title || 'Item') + ' added to your bag.';
          clearError();
          if (label) label.textContent = label.dataset.addedLabel || 'Added';
          announce(status, message);
          showToast(message);
          await new Promise((resolve) => window.setTimeout(resolve, 500));
        } catch (error) {
          if (error.name === 'AbortError') return;
          failed = true;
          const message = error.message || 'This item could not be added. Please try again.';
          if (checkoutError) { checkoutError.textContent = message; checkoutError.hidden = false; }
          reportError(message);
        } finally {
          delete form.dataset.addPending;
          form.removeAttribute('aria-busy');
          pendingControlStates.forEach(({ control, disabled }) => {
            if (control.isConnected) control.disabled = disabled;
          });
          if (button?.isConnected) {
            button.removeAttribute('aria-busy');
            const currentVariantId = form.querySelector('[name="id"]')?.value || '';
            const variantIsUnchanged = !submittedVariantId || currentVariantId === submittedVariantId;
            const currentOption = form.querySelector('[data-product-variant-select] option:checked');
            const currentAvailable = currentOption
              ? currentOption.dataset.variantAvailable === 'true'
              : !originalDisabled;
            button.disabled = variantIsUnchanged ? originalDisabled : !currentAvailable;
            if (label) {
              label.textContent = button.disabled
                ? (label.dataset.soldOutLabel || 'Sold out')
                : (label.dataset.availableLabel || originalLabel || 'Add to bag');
            }
            if (failed) {
              window.requestAnimationFrame(() => {
                const focusTarget = button.disabled
                  ? form.querySelector('[data-product-option-input]:checked')
                  : button;
                focusTarget?.focus?.({ preventScroll: true });
              });
            }
          }
        }
      });
    };

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      requestController.abort();
      setBusy(false);
      overlay.destroy();
      overlayInstances.delete(backdrop);
      cartInstances.delete(backdrop);
      if (statusResult.created) status?.remove();
    };

    const api = {
      overlay,
      changeLine,
      shouldEnhanceForm,
      addFromForm,
      renderCart,
      destroy
    };
    cartInstances.set(backdrop, api);
    return api;
  };

  const createMenuController = (menu) => {
    if (!menu.hasAttribute('role')) menu.setAttribute('role', 'dialog');
    menu.setAttribute('aria-modal', 'true');
    const overlay = createOverlayController({
      root: menu,
      dialog: menu,
      openSelector: '[data-menu-open]',
      closeSelector: '[data-menu-close]',
      initialFocusSelector: '[data-menu-close], [data-overlay-initial-focus]'
    });
    const api = {
      overlay,
      destroy: () => {
        overlay.destroy();
        overlayInstances.delete(menu);
        menuInstances.delete(menu);
      }
    };
    menuInstances.set(menu, api);
    return api;
  };

  const addSwipe = (surface, previous, next, signal) => {
    if (!surface) return;
    let start = null;
    let suppressClick = false;
    let suppressTimer = null;

    listen(surface, 'pointerdown', (event) => {
      if (!event.isPrimary || event.pointerType === 'mouse') return;
      start = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }, { passive: true }, signal);

    listen(surface, 'pointerup', (event) => {
      if (!start || event.pointerId !== start.id) return;
      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      start = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY) * 1.15) return;
      suppressClick = true;
      window.clearTimeout(suppressTimer);
      suppressTimer = window.setTimeout(() => { suppressClick = false; }, 350);
      if (deltaX < 0) next();
      else previous();
    }, { passive: true }, signal);

    listen(surface, 'pointercancel', () => { start = null; }, { passive: true }, signal);
    listen(surface, 'click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true, signal);
    signal?.addEventListener('abort', () => window.clearTimeout(suppressTimer), { once: true });
  };

  const createHeroController = (section) => {
    const events = new AbortController();
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const slides = [...section.querySelectorAll('[data-hero-slide]')];
    const picks = [...section.querySelectorAll('[data-hero-pick], [data-demo-hero-pick]')];
    const previousButtons = [...section.querySelectorAll('[data-hero-prev]')];
    const nextButtons = [...section.querySelectorAll('[data-hero-next]')];
    const toggle = section.querySelector('[data-hero-toggle]');
    const image = section.querySelector('[data-hero-image]');
    const statusResult = ensureStatus(section, '[data-hero-status]', 'data-hero-status');
    const status = statusResult.node;
    const originalLabelledBy = section.getAttribute('aria-labelledby');
    const legacyLiveRegions = [...section.querySelectorAll('[aria-live]')]
      .filter((node) => node !== status);
    const legacyLiveValues = new Map(legacyLiveRegions.map((node) => [node, node.getAttribute('aria-live')]));
    const motionElements = [...section.querySelectorAll('[data-hero-motion], .orbit, [data-hero-image]')];
    const originalPlayStates = new Map(motionElements.map((node) => [node, node.style.animationPlayState]));
    const autoplayByDefault = section.dataset.autoplay === 'true';
    const rawDelay = Number(section.dataset.delay || 6500);
    const delay = Math.max(3000, rawDelay < 1000 ? rawDelay * 1000 : rawDelay);
    const count = slides.length || picks.length;
    const heroItems = slides.length ? slides : picks;
    const explicitlyActive = heroItems.findIndex((node) => (
      node.classList.contains('is-active') || node.getAttribute('aria-current') === 'true'
    ));
    const visiblyActive = heroItems.findIndex((node) => !node.hidden);
    let activeIndex = Math.max(0, explicitlyActive !== -1 ? explicitlyActive : visiblyActive);
    const autoplayProgress = section.querySelector('[data-hero-progress]');
    let progressAnimation = null;
    let timer = null;
    let userPaused = !autoplayByDefault;
    let pointerInside = false;
    let focusWithin = section.contains(document.activeElement);
    let destroyed = false;
    let imageToken = 0;
    let demoData = [];

    legacyLiveRegions.forEach((node) => node.setAttribute('aria-live', 'off'));
    try {
      const json = section.parentElement?.querySelector('[data-demo-hero-data]')?.textContent || '[]';
      demoData = JSON.parse(json);
    } catch (_) {
      demoData = [];
    }

    const normalizeIndex = (index) => {
      if (!count) return 0;
      return (Number(index) % count + count) % count;
    };

    const controlIndex = (control, fallback) => {
      const explicit = control?.dataset.heroIndex ?? control?.dataset.slideIndex ?? control?.dataset.index;
      if (explicit != null && explicit !== '') {
        const numeric = Number(explicit);
        if (Number.isFinite(numeric)) return normalizeIndex(numeric);
      }
      const controls = control?.getAttribute?.('aria-controls');
      if (controls && slides.length) {
        const index = slides.findIndex((slide) => slide.id === controls);
        if (index !== -1) return index;
      }
      return normalizeIndex(fallback);
    };

    const itemTitle = (index, data) => {
      if (data?.title) return data.title;
      const slide = slides[index];
      return slide?.dataset.title
        || slide?.querySelector('[data-hero-title], h1, h2, h3')?.textContent?.trim()
        || picks[index]?.querySelector('strong')?.textContent?.trim()
        || 'Featured item';
    };

    const updateLegacyPresentation = (data) => {
      if (!data) return;
      const title = section.querySelector('[data-hero-title]');
      const category = section.querySelector('[data-hero-category]');
      const price = section.querySelector('[data-hero-price]');
      const option = section.querySelector('[data-hero-option]');
      const variant = section.querySelector('[data-hero-variant]');
      const addButton = section.querySelector('[data-hero-add], [data-hero-form] [type="submit"]');
      if (title) title.textContent = data.title || '';
      if (category) category.textContent = data.category || 'HALLOWEEN FAVORITE';
      if (price) price.textContent = data.price || '';
      if (option) option.textContent = data.option || '';
      if (variant && data.variant) variant.value = data.variant;
      if (addButton && data.available != null) {
        addButton.disabled = String(data.available) === 'false';
      }
      section.querySelectorAll('[data-hero-link]').forEach((link) => {
        if (data.url) link.href = data.url;
        if (data.title) link.setAttribute('aria-label', 'View ' + data.title);
      });

      if (!image || !data.image) return;
      const token = ++imageToken;
      const probe = new Image();
      probe.sizes = image.sizes || '(min-width: 900px) 44vw, 88vw';
      if (data.imageSrcset) probe.srcset = data.imageSrcset;
      section.setAttribute('aria-busy', 'true');
      probe.onload = async () => {
        if (destroyed || token !== imageToken) return;
        try {
          await probe.decode?.();
        } catch (_) {
          // The loaded bitmap can still be presented.
        }
        if (destroyed || token !== imageToken) return;
        image.classList.remove('is-switching');
        image.removeAttribute('srcset');
        if (data.imageSrcset) image.srcset = data.imageSrcset;
        image.src = data.image;
        image.alt = data.imageAlt || data.title || '';
        if (!motionQuery.matches) {
          window.requestAnimationFrame(() => {
            if (!destroyed && token === imageToken) image.classList.add('is-switching');
          });
        }
        section.removeAttribute('aria-busy');
      };
      probe.onerror = () => {
        if (token === imageToken) section.removeAttribute('aria-busy');
      };
      probe.src = data.image;
    };

    const updateControls = () => {
      picks.forEach((pick, fallback) => {
        const selected = controlIndex(pick, fallback) === activeIndex;
        pick.classList.toggle('is-active', selected);
        pick.setAttribute('aria-pressed', selected ? 'true' : 'false');
        pick.setAttribute('aria-current', selected ? 'true' : 'false');
      });
      const current = section.querySelector('[data-hero-current]');
      const total = section.querySelector('[data-hero-total]');
      const progress = section.querySelector('[data-hero-progress]');
      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      if (total) total.textContent = String(count).padStart(2, '0');
      if (progress && count) progress.style.width = '100%';
      previousButtons.forEach((button) => { button.disabled = count < 2; });
      nextButtons.forEach((button) => { button.disabled = count < 2; });
    };

    const pausePanelMedia = (panel) => {
      panel.querySelectorAll('video').forEach((video) => video.pause?.());
      panel.querySelectorAll('iframe').forEach((frame) => {
        frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
        frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
      });
    };

    const activate = (requestedIndex, { manual = false } = {}) => {
      if (!count || destroyed) return;
      activeIndex = normalizeIndex(requestedIndex);
      let data = null;
      if (slides.length) {
        slides.forEach((slide, index) => {
          const active = index === activeIndex;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          if (!active) pausePanelMedia(slide);
        });
        const activeLabelledBy = slides[activeIndex]?.getAttribute('aria-labelledby');
        if (activeLabelledBy) section.setAttribute('aria-labelledby', activeLabelledBy);
      } else {
        const pick = picks[activeIndex];
        data = pick?.matches('[data-hero-pick]')
          ? pick.dataset
          : demoData[Number(pick?.dataset.demoIndex || activeIndex)];
        updateLegacyPresentation(data);
      }
      updateControls();
      if (manual) {
        announce(status, 'Item ' + (activeIndex + 1) + ' of ' + count + ': ' + itemTitle(activeIndex, data));
      }
    };

    const resetAutoplayProgress = () => {
      if (progressAnimation) progressAnimation.cancel();
      progressAnimation = null;
      if (!autoplayProgress) return;
      autoplayProgress.style.width = '100%';
      autoplayProgress.style.transformOrigin = 'left center';
      autoplayProgress.style.transform = 'scaleX(0)';
    };
    const runAutoplayProgress = () => {
      resetAutoplayProgress();
      if (!autoplayProgress) return;
      progressAnimation = autoplayProgress.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: delay, easing: 'linear', fill: 'forwards' }
      );
    };
    const stopAutoplay = () => {
      window.clearTimeout(timer);
      timer = null;
      resetAutoplayProgress();
    };

    const resumeToggleIsActive = () => (
      !userPaused && toggle instanceof HTMLElement && document.activeElement === toggle
    );

    const autoplayBlocked = () => (
      userPaused

      
      || document.hidden
      || motionQuery.matches
      || destroyed
      || count < 2
    );

    const startAutoplay = () => {
      if (autoplayBlocked() || timer) return;
      runAutoplayProgress();
      timer = window.setTimeout(() => {
        timer = null;
        if (autoplayBlocked()) return;
        activate(activeIndex + 1);
        startAutoplay();
      }, delay);
    };

    const updateToggle = () => {
      if (!toggle) return;
      toggle.hidden = count < 2 || motionQuery.matches;
      const label = userPaused
        ? (toggle.dataset.playLabel || 'Resume autoplay')
        : (toggle.dataset.pauseLabel || 'Pause autoplay');
      const labelNode = toggle.querySelector('[data-hero-toggle-label]');
      if (labelNode) labelNode.textContent = label;
      else toggle.textContent = label;
      toggle.setAttribute('aria-label', label);
      toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      toggle.dataset.paused = userPaused ? 'true' : 'false';
    };

    const updateDecorativeMotion = () => {
      const paused = userPaused || motionQuery.matches;
      section.classList.toggle('is-motion-paused', paused);
      motionElements.forEach((element) => {
        element.style.animationPlayState = paused ? 'paused' : originalPlayStates.get(element);
      });
    };

    const pauseAfterInteraction = () => {
      stopAutoplay();
      if (autoplayByDefault) {
        userPaused = false;
        updateToggle();
        updateDecorativeMotion();
        queueMicrotask(() => startAutoplay());
        return;
      }
      userPaused = true;
      updateToggle();
      updateDecorativeMotion();
    };

    picks.forEach((pick, fallback) => {
      listen(pick, 'click', () => {
        pauseAfterInteraction();
        activate(controlIndex(pick, fallback), { manual: true });
      }, false, events.signal);
    });
    previousButtons.forEach((button) => {
      listen(button, 'click', () => {
        pauseAfterInteraction();
        activate(activeIndex - 1, { manual: true });
      }, false, events.signal);
    });
    nextButtons.forEach((button) => {
      listen(button, 'click', () => {
        pauseAfterInteraction();
        activate(activeIndex + 1, { manual: true });
      }, false, events.signal);
    });
    listen(toggle, 'click', () => {
      userPaused = !userPaused;
      updateToggle();
      updateDecorativeMotion();
      if (userPaused) stopAutoplay();
      else startAutoplay();
    }, false, events.signal);
    listen(section, 'pointerenter', () => {
      pointerInside = true;
    }, false, events.signal);
    listen(section, 'pointerleave', () => {
      pointerInside = false;
      startAutoplay();
    }, false, events.signal);
    listen(section, 'focusin', () => {
      focusWithin = true;
    }, false, events.signal);
    listen(section, 'focusout', (event) => {
      focusWithin = Boolean(event.relatedTarget && section.contains(event.relatedTarget));
      if (!focusWithin) startAutoplay();
    }, false, events.signal);
    listen(section, 'keydown', (event) => {
      if (/^(INPUT|SELECT|TEXTAREA)$/.test(event.target.tagName)) return;
      let targetIndex = null;
      if (event.key === 'ArrowLeft') targetIndex = activeIndex - 1;
      if (event.key === 'ArrowRight') targetIndex = activeIndex + 1;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = count - 1;
      if (targetIndex == null) return;
      event.preventDefault();
      pauseAfterInteraction();
      activate(targetIndex, { manual: true });
      const activePick = picks.find((pick, fallback) => controlIndex(pick, fallback) === activeIndex);
      activePick?.focus?.();
    }, false, events.signal);
    listen(document, 'visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    }, false, events.signal);
    listenToMediaQuery(motionQuery, () => {
      if (motionQuery.matches) stopAutoplay();
      updateToggle();
      updateDecorativeMotion();
      if (!motionQuery.matches) startAutoplay();
    }, events.signal);

    const swipeSurface = section.querySelector(
      '[data-swipe-root], [data-hero-track], [data-hero-swipe], [data-hero-stage], .hero-product'
    ) || section;
    addSwipe(
      swipeSurface,
      () => {
        pauseAfterInteraction();
        activate(activeIndex - 1, { manual: true });
      },
      () => {
        pauseAfterInteraction();
        activate(activeIndex + 1, { manual: true });
      },
      events.signal
    );

    activate(activeIndex);
    updateToggle();
    updateDecorativeMotion();
    startAutoplay();

    return {
      selectSlide: (requestedIndex) => {
        if (destroyed) return;
        userPaused = true;
        stopAutoplay();
        activate(requestedIndex);
        updateToggle();
        updateDecorativeMotion();
      },
      destroy: () => {
        if (destroyed) return;
        destroyed = true;
        imageToken += 1;
        stopAutoplay();
        events.abort();
        section.removeAttribute('aria-busy');
        section.classList.remove('is-motion-paused');
        originalPlayStates.forEach((value, element) => {
          element.style.animationPlayState = value;
        });
        legacyLiveValues.forEach((value, node) => {
          if (value == null) node.removeAttribute('aria-live');
          else node.setAttribute('aria-live', value);
        });
        if (originalLabelledBy == null) section.removeAttribute('aria-labelledby');
        else section.setAttribute('aria-labelledby', originalLabelledBy);
        if (statusResult.created) status?.remove();
      }
    };
  };

  const resolveMotionRegion = (toggle) => {
    const controlledId = toggle.getAttribute('aria-controls');
    if (controlledId) {
      const controlled = document.getElementById(controlledId);
      if (controlled?.matches('[data-motion-region]')) return controlled;
    }
    return toggle.closest('[data-motion-region], .announcement-ticker, .marquee');
  };

  const createMotionController = (region) => {
    const events = new AbortController();
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const toggles = [...region.querySelectorAll('[data-motion-toggle]')];
    const animated = [...region.querySelectorAll(
      '[data-motion-track], [data-motion-animated], .announcement-track, .marquee-track'
    )];
    const originalStates = new Map(animated.map((element) => [element, element.style.animationPlayState]));
    const transientPauseEnabled = !region.matches('[data-marquee], .mm-marquee')
      || region.classList.contains('mm-marquee--pause-on-hover');
    let userPaused = false;
    let transientPaused = false;

    const apply = () => {
      const resumeToggleIsActive = !userPaused && toggles.some((item) => item === document.activeElement);
      const paused = userPaused || (!resumeToggleIsActive && transientPaused) || motionQuery.matches;
      region.classList.toggle('is-motion-paused', paused);
      animated.forEach((element) => {
        element.style.animationPlayState = paused ? 'paused' : originalStates.get(element);
      });
      toggles.forEach((toggle) => {
        toggle.hidden = motionQuery.matches;
        toggle.dataset.paused = userPaused ? 'true' : 'false';
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        const label = userPaused
          ? (toggle.dataset.playLabel || 'Resume motion')
          : (toggle.dataset.pauseLabel || 'Pause motion');
        const labelNode = toggle.querySelector('[data-motion-label], [data-motion-toggle-label]');
        if (labelNode) labelNode.textContent = label;
        else toggle.textContent = label;
        toggle.setAttribute('aria-label', label);
      });
    };

    toggles.forEach((toggle) => {
      listen(toggle, 'click', () => {
        userPaused = !userPaused;
        apply();
      }, false, events.signal);
    });
    listen(region, 'pointerenter', () => {
      if (!transientPauseEnabled) return;
      transientPaused = true;
      apply();
    }, false, events.signal);
    listen(region, 'pointerleave', () => {
      if (!transientPauseEnabled) return;
      transientPaused = false;
      apply();
    }, false, events.signal);
    listen(region, 'focusin', () => {
      if (!transientPauseEnabled) return;
      transientPaused = true;
      apply();
    }, false, events.signal);
    listen(region, 'focusout', (event) => {
      if (!transientPauseEnabled) return;
      transientPaused = Boolean(event.relatedTarget && region.contains(event.relatedTarget));
      apply();
    }, false, events.signal);
    listenToMediaQuery(motionQuery, apply, events.signal);
    apply();

    return {
      destroy: () => {
        events.abort();
        region.classList.remove('is-motion-paused');
        originalStates.forEach((value, element) => {
          element.style.animationPlayState = value;
        });
      }
    };
  };

  const createCatalogController = (catalog) => {
    const events = new AbortController();
    const buttons = [...catalog.querySelectorAll('[data-product-filter]')];
    const cards = [...catalog.querySelectorAll('[data-product-card]')];
    const count = catalog.querySelector('[data-visible-count]');
    count?.parentElement?.setAttribute('aria-live', 'polite');
    buttons.forEach((button) => {
      listen(button, 'click', () => {
        const filter = button.dataset.productFilter;
        buttons.forEach((item) => {
          const selected = item === button;
          item.classList.toggle('active', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        let visible = 0;
        cards.forEach((card) => {
          const show = filter === 'all' || card.dataset.productType === filter;
          card.hidden = !show;
          if (show) visible += 1;
        });
        if (count) count.textContent = String(visible);
      }, false, events.signal);
    });
    return { destroy: () => events.abort() };
  };

  const createFacetsController = (form) => {
    const shell = form.querySelector('[data-facets-shell]');
    const dialog = shell?.querySelector('[data-facets-dialog]');
    const openButton = form.querySelector('[data-facets-open]');
    if (!shell || !dialog || !openButton) return { destroy: () => {} };

    const events = new AbortController();
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const labelledBy = dialog.querySelector('h2[id]')?.id;
    const overlay = createOverlayController({
      root: shell,
      dialog,
      openSelector: '[data-facets-open]',
      closeSelector: '[data-facets-close]',
      initialFocusSelector: '.mm-facets__close'
    });

    const syncMode = () => {
      const wasOpen = overlayStack.includes(overlay);
      const focusWasInside = shell.contains(document.activeElement);
      if (wasOpen) overlay.close({ restoreFocus: false });
      if (mobileQuery.matches) {
        shell.hidden = true;
        shell.setAttribute('aria-hidden', 'true');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
        if (labelledBy) dialog.setAttribute('aria-labelledby', labelledBy);
        openButton.setAttribute('aria-expanded', 'false');
      } else {
        shell.hidden = false;
        shell.removeAttribute('aria-hidden');
        dialog.removeAttribute('role');
        dialog.removeAttribute('aria-modal');
        dialog.removeAttribute('aria-labelledby');
        openButton.setAttribute('aria-expanded', 'false');
      }
      if (wasOpen && !mobileQuery.matches) {
        window.requestAnimationFrame(() => {
          const desktopTarget = dialog.querySelector('summary')
            || dialog.querySelector('input:not([type="hidden"]), select')
            || dialog;
          desktopTarget.focus?.({ preventScroll: true });
        });
      } else if (focusWasInside && mobileQuery.matches) {
        window.requestAnimationFrame(() => openButton.focus({ preventScroll: true }));
      }
    };

    listen(openButton, 'click', () => {
      if (mobileQuery.matches) overlay.open(openButton);
    }, false, events.signal);
    shell.querySelectorAll('[data-facets-close]').forEach((control) => {
      listen(control, 'click', () => {
        if (mobileQuery.matches) overlay.close();
      }, false, events.signal);
    });
    listenToMediaQuery(mobileQuery, syncMode, events.signal);
    syncMode();
    form.classList.add('facets-enhanced');

    return {
      destroy: () => {
        events.abort();
        overlay.destroy();
        overlayInstances.delete(shell);
        shell.hidden = false;
        shell.removeAttribute('aria-hidden');
        dialog.removeAttribute('role');
        dialog.removeAttribute('aria-modal');
        dialog.removeAttribute('aria-labelledby');
        form.classList.remove('facets-enhanced');
      }
    };
  };

  const stopEmbeddedMedia = (panel) => {
    panel.querySelectorAll('video').forEach((video) => video.pause?.());
    panel.querySelectorAll('iframe').forEach((frame) => {
      frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
      frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    });
    panel.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
  };

  const createProductController = (section) => {
    const events = new AbortController();
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const quantity = section.querySelector('input[name="quantity"]');
    const gallery = section.querySelector('[data-product-gallery]');
    const panels = gallery ? [...gallery.querySelectorAll('[data-media-id].product-media, [data-product-media][data-media-id]')] : [];
    const thumbs = gallery ? [...gallery.querySelectorAll('[data-media-thumb]')] : [];
    const thumbnailRail = gallery?.querySelector('[data-media-thumbnail-rail]');
    const thumbnailTrack = gallery?.querySelector('[data-media-thumbnails]');
    const thumbnailPrevious = gallery?.querySelector('[data-thumbnails-prev]');
    const thumbnailNext = gallery?.querySelector('[data-thumbnails-next]');
    const updateThumbnailNavigation = () => {
      if (!thumbnailTrack || !thumbnailRail) return;
      const overflow = thumbnailTrack.scrollWidth > thumbnailTrack.clientWidth + 2;
      thumbnailRail.classList.toggle('has-overflow', overflow);
      const maximum = Math.max(0, thumbnailTrack.scrollWidth - thumbnailTrack.clientWidth);
      const slider = gallery.querySelector('[data-media-slider]');
      const selected = Math.max(0, thumbs.findIndex(thumb => thumb.getAttribute('aria-pressed') === 'true'));
      if (thumbnailPrevious) {
        thumbnailPrevious.hidden = thumbs.length < 2;
        thumbnailPrevious.disabled = slider ? selected === 0 : thumbnailTrack.scrollLeft <= 2;
      }
      if (thumbnailNext) {
        thumbnailNext.hidden = thumbs.length < 2;
        thumbnailNext.disabled = slider ? selected === thumbs.length - 1 : thumbnailTrack.scrollLeft >= maximum - 2;
      }
    };
    const scrollThumbnails = (direction) => {
      if (!thumbnailTrack) return;
      if (mediaStage?.hasAttribute('data-media-slider')) {
        activateMedia(Math.max(0, Math.min(panels.length - 1, activeMediaIndex + direction)), { manual: true, byIndex: true });
        return;
      }
      thumbnailTrack.scrollBy({
        left: direction * thumbnailTrack.clientWidth * .8,
        behavior: motionQuery.matches ? 'auto' : 'smooth'
      });
    };
    listen(thumbnailPrevious, 'click', () => scrollThumbnails(-1), false, events.signal);
    listen(thumbnailNext, 'click', () => scrollThumbnails(1), false, events.signal);
    listen(thumbnailTrack, 'scroll', updateThumbnailNavigation, { passive: true }, events.signal);
    listen(window, 'resize', updateThumbnailNavigation, { passive: true }, events.signal);
    const thumbnailResizeObserver = thumbnailTrack && window.ResizeObserver
      ? new ResizeObserver(updateThumbnailNavigation) : null;
    if (thumbnailResizeObserver) thumbnailResizeObserver.observe(thumbnailTrack);
    updateThumbnailNavigation();
    const previousButtons = gallery ? [...gallery.querySelectorAll('[data-media-prev], [data-gallery-prev]')] : [];
    const nextButtons = gallery ? [...gallery.querySelectorAll('[data-media-next], [data-gallery-next]')] : [];
    const counter = gallery?.querySelector('[data-media-counter], [data-gallery-counter]');
    const currentCounter = gallery?.querySelector('[data-media-current]');
    const totalCounter = gallery?.querySelector('[data-media-total]');
    const swapIndicator = gallery?.querySelector('[data-media-swap]');
    const mediaStage = gallery?.querySelector('[data-product-media-stage], [data-gallery-stage]');
    const slidingMedia = Boolean(mediaStage?.hasAttribute('data-media-slider'));
    let mediaScrollTimer = 0;
    let mediaDrag = null;
    const zoomTriggers = gallery ? [...gallery.querySelectorAll('[data-media-zoom-open]')] : [];
    const mediaModal = section.querySelector('[data-product-media-modal]');
    const mediaModalDialog = mediaModal?.querySelector('[data-product-media-dialog]');
    const mediaModalImage = mediaModal?.querySelector('[data-product-media-modal-image]');
    const form = section.querySelector('.product-form');
    const select = section.querySelector('[data-product-variant-select]');
    const variantFallback = section.querySelector('[data-variant-select-fallback]');
    const variantFallbackWasHidden = Boolean(variantFallback?.hidden);
    const variantFallbackAriaHidden = variantFallback?.getAttribute('aria-hidden');
    const selectAriaHidden = select?.getAttribute('aria-hidden');
    const selectTabIndex = select?.getAttribute('tabindex');
    const jsonNode = section.parentElement?.querySelector('[data-product-json]')
      || section.querySelector('[data-product-json]');
    const variantStatusResult = ensureStatus(
      form || section.querySelector('[data-product-info]') || section,
      '[data-product-form-status], [data-variant-status]',
      'data-variant-status'
    );
    const variantStatus = variantStatusResult.node;
    const initialVariantId = String(section.dataset.selectedVariantId || '');
    const mediaModalOverlay = mediaModal && mediaModalDialog
      ? createOverlayController({
        root: mediaModal,
        dialog: mediaModalDialog,
        openSelector: '[data-product-media-modal-open-never]',
        closeSelector: '[data-media-zoom-close]',
        initialFocusSelector: '.mm-product-media-modal__close'
      })
      : null;
    let activeMediaIndex = Math.max(0, panels.findIndex((panel) => (
      panel.classList.contains('is-active') || !panel.hidden
    )));
    let destroyed = false;
    let product = null;

    try {
      if (jsonNode) product = JSON.parse(jsonNode.textContent);
    } catch (_) {
      product = null;
    }

    const hasOptionInput = section.querySelectorAll('[data-product-option-input]').length > 0;
    if (variantFallback && select && product?.variants?.length && hasOptionInput) {
      section.classList.add('is-product-options-enhanced');
      variantFallback.hidden = true;
      variantFallback.setAttribute('aria-hidden', 'true');
      select.setAttribute('aria-hidden', 'true');
      select.setAttribute('tabindex', '-1');
    }

    const normalizeQuantity = () => {
      if (!quantity) return 1;
      const minimum = Math.max(1, Number(quantity.min || 1));
      const maximum = quantity.max ? Number(quantity.max) : Infinity;
      const next = Math.min(maximum, Math.max(minimum, Math.floor(Number(quantity.value) || minimum)));
      quantity.value = String(next);
      return next;
    };

    const changeQuantity = (difference) => {
      if (!quantity) return;
      normalizeQuantity();
      quantity.value = String(Number(quantity.value) + difference);
      normalizeQuantity();
      quantity.dispatchEvent(new Event('input', { bubbles: true }));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    };

    listen(section.querySelector('[data-quantity-minus]'), 'click', () => changeQuantity(-1), false, events.signal);
    listen(section.querySelector('[data-quantity-plus]'), 'click', () => changeQuantity(1), false, events.signal);
    listen(quantity, 'change', normalizeQuantity, false, events.signal);
    listen(quantity, 'blur', normalizeQuantity, false, events.signal);

    zoomTriggers.forEach((trigger) => {
      listen(trigger, 'click', (event) => {
        const source = trigger.querySelector('img');
        if (!source || !mediaModalImage || !mediaModalOverlay) return;
        event.preventDefault();
        mediaModalImage.width = Number(source.getAttribute('width')) || source.naturalWidth || 1;
        mediaModalImage.height = Number(source.getAttribute('height')) || source.naturalHeight || 1;
        mediaModalImage.src = source.currentSrc || source.src;
        if (source.srcset) mediaModalImage.srcset = source.srcset;
        else mediaModalImage.removeAttribute('srcset');
        mediaModalImage.sizes = 'min(94vw, 1600px)';
        mediaModalImage.alt = source.alt || 'Enlarged product image';
        mediaModalOverlay.open(trigger);
      }, false, events.signal);
    });
    mediaModal?.querySelectorAll('[data-media-zoom-close]').forEach((control) => {
      listen(control, 'click', () => mediaModalOverlay?.close(), false, events.signal);
    });

    const mediaIndexForId = (mediaId) => panels.findIndex((panel) => (
      String(panel.dataset.mediaId) === String(mediaId)
    ));

    const activateMedia = (requested, { manual = false, focusThumb = false, byIndex = false, fromScroll = false } = {}) => {
      if (!panels.length || destroyed) return;
      const numeric = Number(requested);
      let index = byIndex && Number.isInteger(numeric) ? numeric : mediaIndexForId(requested);
      if (!byIndex && index < 0) return;
      index = (index % panels.length + panels.length) % panels.length;
      activeMediaIndex = index;
      const activePanel = panels[index];
      const activeId = activePanel.dataset.mediaId;

      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = slidingMedia ? false : !active;
        if (slidingMedia) panel.inert = !active;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          panel.querySelectorAll('iframe[data-src]').forEach((frame) => {
            if (!frame.getAttribute('src')) frame.setAttribute('src', frame.dataset.src);
          });
        } else {
          stopEmbeddedMedia(panel);
        }
      });

      if (slidingMedia && !fromScroll) {
        const offset = activePanel.getBoundingClientRect().left - mediaStage.getBoundingClientRect().left + mediaStage.scrollLeft;
        mediaStage.scrollTo({ left: offset, behavior: motionQuery.matches || !manual ? 'instant' : 'smooth' });
      }
      let activeThumb = null;
      thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === String(activeId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        thumb.tabIndex = active ? 0 : -1;
        if (active) activeThumb = thumb;
      });
      if (counter) {
        counter.textContent = (index + 1) + ' / ' + panels.length;
        if (manual) {
          counter.setAttribute('aria-live', 'polite');
          counter.setAttribute('aria-atomic', 'true');
        }
      }
      if (currentCounter) currentCounter.textContent = String(index + 1);
      if (totalCounter) totalCounter.textContent = String(panels.length);
      if (mediaStage) {
        mediaStage.setAttribute('aria-label', 'Product media, item ' + (index + 1) + ' of ' + panels.length);
      }
      if (swapIndicator) {
        swapIndicator.dataset.mediaId = activeId;
        swapIndicator.classList.remove('is-switching');
        if (!motionQuery.matches) {
          window.requestAnimationFrame(() => swapIndicator.classList.add('is-switching'));
        }
      }
      previousButtons.forEach((button) => { button.disabled = panels.length < 2; });
      nextButtons.forEach((button) => { button.disabled = panels.length < 2; });
      if (activeThumb) {
        if (thumbnailTrack) {
          const trackBounds = thumbnailTrack.getBoundingClientRect();
          const thumbBounds = activeThumb.getBoundingClientRect();
          const leftOverflow = thumbBounds.left - trackBounds.left - 2;
          const rightOverflow = thumbBounds.right - trackBounds.right + 2;
          const shift = leftOverflow < 0 ? leftOverflow : rightOverflow > 0 ? rightOverflow : 0;
          if (shift) thumbnailTrack.scrollBy({ left: shift, behavior: motionQuery.matches || !manual ? 'auto' : 'smooth' });
        }
        if (focusThumb) activeThumb.focus({ preventScroll: true });
      }
      updateThumbnailNavigation();
    };

    thumbs.forEach((thumb) => {
      listen(thumb, 'click', () => activateMedia(thumb.dataset.mediaId, { manual: true }), false, events.signal);
      listen(thumb, 'keydown', (event) => {
        let index = null;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') index = activeMediaIndex - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') index = activeMediaIndex + 1;
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = panels.length - 1;
        if (index == null) return;
        event.preventDefault();
        activateMedia(index, { manual: true, focusThumb: true, byIndex: true });
      }, false, events.signal);
    });
    previousButtons.forEach((button) => {
      listen(button, 'click', () => activateMedia(activeMediaIndex - 1, { manual: true, byIndex: true }), false, events.signal);
    });
    nextButtons.forEach((button) => {
      listen(button, 'click', () => activateMedia(activeMediaIndex + 1, { manual: true, byIndex: true }), false, events.signal);
    });

    const gallerySurface = mediaStage || gallery;
    if (slidingMedia) {
      const syncMediaScroll = () => {
        if (destroyed || mediaDrag) return;
        const left = mediaStage.getBoundingClientRect().left;
        const distances = panels.map(panel => Math.abs(panel.getBoundingClientRect().left - left));
        const index = distances.indexOf(Math.min(...distances));
        if (index !== activeMediaIndex) activateMedia(index, { byIndex: true, manual: true, fromScroll: true });
      };
      listen(mediaStage, 'scroll', () => {
        clearTimeout(mediaScrollTimer);
        mediaScrollTimer = setTimeout(syncMediaScroll, 160);
      }, { passive: true }, events.signal);
      listen(mediaStage, 'scrollend', syncMediaScroll, { passive: true }, events.signal);
      listen(window, 'resize', () => activateMedia(activeMediaIndex, { byIndex: true }), { passive: true }, events.signal);
      listen(mediaStage, 'dragstart', event => event.preventDefault(), false, events.signal);
      listen(mediaStage, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('video,iframe,model-viewer,button,a')) return;
        mediaDrag = { x: event.clientX, left: mediaStage.scrollLeft, id: event.pointerId, moved: false };
      }, false, events.signal);
      listen(window, 'pointermove', event => {
        if (!mediaDrag || mediaDrag.id !== event.pointerId) return;
        const delta = event.clientX - mediaDrag.x;
        if (!mediaDrag.moved && Math.abs(delta) < 5) return;
        mediaDrag.moved = true;
        mediaStage.classList.add('is-dragging');
        mediaStage.scrollLeft = mediaDrag.left - delta;
        event.preventDefault();
      }, { passive: false }, events.signal);
      const finishMediaDrag = event => {
        if (!mediaDrag || mediaDrag.id !== event.pointerId) return;
        const drag = mediaDrag;
        mediaDrag = null;
        mediaStage.classList.remove('is-dragging');
        if (!drag.moved) return;
        const delta = event.clientX - drag.x;
        const shift = Math.abs(delta) > 35 ? (delta < 0 ? 1 : -1) : 0;
        const index = Math.max(0, Math.min(panels.length - 1, Math.round(drag.left / mediaStage.clientWidth) + shift));
        activateMedia(index, { byIndex: true, manual: true });
      };
      listen(window, 'pointerup', finishMediaDrag, false, events.signal);
      listen(window, 'pointercancel', finishMediaDrag, false, events.signal);
    }
    if (panels.length > 1) {
      if (!slidingMedia) addSwipe(
        gallerySurface,
        () => activateMedia(activeMediaIndex - 1, { manual: true, byIndex: true }),
        () => activateMedia(activeMediaIndex + 1, { manual: true, byIndex: true }),
        events.signal
      );

      listen(gallerySurface, 'keydown', (event) => {
        if (event.target.closest?.('[data-media-thumb], video, iframe, model-viewer')) return;
        let index = null;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') index = activeMediaIndex - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') index = activeMediaIndex + 1;
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = panels.length - 1;
        if (index == null) return;
        event.preventDefault();
        activateMedia(index, { manual: true, byIndex: true });
      }, false, events.signal);
    }

    const updateOptionAvailability = () => {
      if (!product?.variants) return;
      const inputs = [...section.querySelectorAll('[data-product-option-input]')];
      const selected = new Map();
      inputs.forEach((input) => {
        if (input.checked) {
          selected.set(Number(input.dataset.optionPosition) - 1, input.dataset.optionValue || input.value);
        }
      });
      inputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const candidate = input.dataset.optionValue || input.value;
        const possible = product.variants.some((variant) => (
          variant.available
          && String(variant.options[position]) === String(candidate)
          && [...selected].every(([selectedPosition, selectedValue]) => (
            selectedPosition >= position
            || String(variant.options[selectedPosition]) === String(selectedValue)
          ))
        ));
        input.disabled = !possible && !input.checked;
        const label = input.id ? section.querySelector('label[for="' + input.id + '"]') : null;
        label?.classList.toggle('is-unavailable', !possible);
        if (label) label.setAttribute('aria-disabled', possible ? 'false' : 'true');
        const unavailableText = label?.querySelector('.visually-hidden');
        if (unavailableText && /unavailable/i.test(unavailableText.textContent || '')) {
          unavailableText.hidden = possible;
        }
      });
    };

    const updateSelectedOptionLabels = () => {
      section.querySelectorAll('[data-product-option-input]:checked').forEach((input) => {
        const position = input.dataset.optionPosition;
        const selected = section.querySelector(
          '[data-product-option][data-option-position="' + position + '"] [data-selected-option]'
        );
        if (selected) selected.textContent = input.dataset.optionValue || input.value;
      });
    };

    const syncOptionInputsToVariant = (variant) => {
      if (!Array.isArray(variant?.options)) return;
      section.querySelectorAll('[data-product-option-input]').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        if (!Number.isInteger(position) || position < 0 || position >= variant.options.length) return;
        const inputValue = input.dataset.optionValue || input.value;
        input.checked = String(inputValue) === String(variant.options[position]);
      });
    };

    const setMoney = (node, cents) => {
      if (node) node.innerHTML = formatMoney(cents);
    };

    const updateVariant = (variant, { announceChange = true, historyMode = 'replace' } = {}) => {
      if (!variant) return;
      const button = section.querySelector('[data-add-to-cart]');
      const label = section.querySelector('[data-add-label]');
      const currentPrice = section.querySelector('[data-price-current]');
      const comparePrice = section.querySelector('[data-price-compare]');
      const saving = section.querySelector('[data-price-saving]');
      const availability = section.querySelector('[data-product-availability]');
      const skuRow = section.querySelector('.product-sku');
      const sku = section.querySelector('[data-product-sku]');
      const hasSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
      setMoney(currentPrice, variant.price);
      if (comparePrice) {
        comparePrice.hidden = !hasSale;
        comparePrice.innerHTML = hasSale ? formatMoney(variant.compare_at_price) : '';
      }
      if (saving) {
        saving.hidden = !hasSale;
        saving.textContent = hasSale ? 'Save ' + moneyText(variant.compare_at_price - variant.price) : '';
      }
      const addPending = form?.dataset.addPending === 'true';
      if (button && !addPending) button.disabled = !variant.available;
      if (label && !addPending) {
        label.textContent = variant.available
          ? (label.dataset.availableLabel || 'Add to bag')
          : (label.dataset.soldOutLabel || 'Sold out');
      }
      if (availability) {
        availability.textContent = variant.available ? 'Available to order' : 'Sold out';
        availability.classList.toggle('is-available', Boolean(variant.available));
      }
      if (sku) sku.textContent = variant.sku || '';
      if (skuRow) skuRow.hidden = !variant.sku;
      activateMedia(variant.featured_media?.id || variant.featured_image?.id);

      if (select && String(select.value) !== String(variant.id)) select.value = String(variant.id);
      const hiddenId = form?.querySelector('input[name="id"]');
      if (hiddenId && !select) hiddenId.value = String(variant.id);
      section.dataset.selectedVariantId = String(variant.id);

      syncOptionInputsToVariant(variant);
      updateSelectedOptionLabels();
      updateOptionAvailability();

      if (historyMode !== 'none' && window.history.replaceState) {
        const url = new URL(window.location.href);
        const currentVariantParameter = url.searchParams.get('variant');
        if (String(currentVariantParameter || '') !== String(variant.id)) {
          url.searchParams.set('variant', variant.id);
          const state = Object.assign({}, window.history.state || {}, { mmVariantId: String(variant.id) });
          if (historyMode === 'push' && window.history.pushState) window.history.pushState(state, '', url.toString());
          else window.history.replaceState(state, '', url.toString());
        }
      }
      if (announceChange) {
        const message = [
          variant.title && variant.title !== 'Default Title' ? variant.title + '.' : '',
          moneyText(variant.price) + '.',
          variant.available ? 'Available to order.' : 'Sold out.',
          variant.sku ? 'SKU ' + variant.sku + '.' : ''
        ].filter(Boolean).join(' ');
        announce(variantStatus, message);
      }
      section.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant }
      }));
    };

    const variantFromOptions = () => {
      if (!product?.variants) return null;
      const fields = [...section.querySelectorAll('[data-product-option-input]')];
      if (!fields.length) return null;
      const byPosition = new Map();
      const sequential = [];
      fields.forEach((field) => {
        if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
        const position = Number(field.dataset.optionPosition);
        const value = field.dataset.optionValue || field.value;
        if (position) byPosition.set(position - 1, value);
        else if (!sequential.includes(value)) sequential.push(value);
      });
      const values = byPosition.size
        ? [...Array(Math.max(...byPosition.keys()) + 1)].map((_, index) => byPosition.get(index))
        : sequential;
      return product.variants.find((variant) => (
        values.length && values.every((value, index) => String(variant.options[index]) === String(value))
      ));
    };

    listen(section, 'change', (event) => {
      if (!product?.variants) return;
      if (event.target.matches('[data-product-variant-select]')) {
        updateVariant(product.variants.find((variant) => String(variant.id) === event.target.value), { historyMode: 'push' });
      } else if (event.target.matches('[data-product-option-input]')) {
        updateSelectedOptionLabels();
        const variant = variantFromOptions();
        if (variant) {
          updateVariant(variant, { historyMode: 'push' });
        } else {
          const button = section.querySelector('[data-add-to-cart]');
          const label = section.querySelector('[data-add-label]');
          const availability = section.querySelector('[data-product-availability]');
          if (button && form?.dataset.addPending !== 'true') button.disabled = true;
          if (label && form?.dataset.addPending !== 'true') {
            label.textContent = label.dataset.unavailableLabel || 'Unavailable';
          }
          if (availability) {
            availability.textContent = 'Unavailable combination';
            availability.classList.remove('is-available');
          }
          updateOptionAvailability();
          announce(variantStatus, 'This option combination is unavailable.');
        }
      }
    }, false, events.signal);

    listen(window, 'popstate', () => {
      if (!product?.variants?.length) return;
      const requestedId = new URL(window.location.href).searchParams.get('variant') || initialVariantId;
      const variant = product.variants.find((item) => String(item.id) === String(requestedId));
      if (variant) updateVariant(variant, { announceChange: false, historyMode: 'none' });
    }, false, events.signal);

    if (panels.length) activateMedia(activeMediaIndex, { byIndex: true });
    updateOptionAvailability();

    return {
      activateMedia,
      updateVariant,
      destroy: () => {
        if (destroyed) return;
        destroyed = true;
        events.abort();
        thumbnailResizeObserver?.disconnect();
        clearTimeout(mediaScrollTimer);
        panels.forEach(stopEmbeddedMedia);
        mediaModalOverlay?.destroy();
        if (mediaModal) overlayInstances.delete(mediaModal);
        if (mediaModalImage) {
          mediaModalImage.removeAttribute('src');
          mediaModalImage.removeAttribute('srcset');
        }
        section.classList.remove('is-product-options-enhanced');
        if (variantFallback) {
          variantFallback.hidden = variantFallbackWasHidden;
          if (variantFallbackAriaHidden == null) variantFallback.removeAttribute('aria-hidden');
          else variantFallback.setAttribute('aria-hidden', variantFallbackAriaHidden);
        }
        if (select) {
          if (selectAriaHidden == null) select.removeAttribute('aria-hidden');
          else select.setAttribute('aria-hidden', selectAriaHidden);
          if (selectTabIndex == null) select.removeAttribute('tabindex');
          else select.setAttribute('tabindex', selectTabIndex);
        }
        if (variantStatusResult.created) variantStatus?.remove();
      }
    };
  };

  const createRevealController = (node, index) => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    node.classList.add('reveal-item');
    node.style.setProperty('--reveal-delay', ((index % 4) * 65) + 'ms');
    if (reducedMotion || !('IntersectionObserver' in window)) {
      node.classList.add('is-visible');
      return { destroy: () => {} };
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -45px' });
    }
    revealObserver.observe(node);
    return {
      destroy: () => revealObserver?.unobserve(node)
    };
  };

  const createCardGlowController = (card) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return { destroy: () => {} };
    }
    const events = new AbortController();
    let frame = null;
    let latestEvent = null;
    listen(card, 'pointermove', (event) => {
      latestEvent = event;
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (!latestEvent) return;
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--pointer-x', (latestEvent.clientX - rect.left) + 'px');
        card.style.setProperty('--pointer-y', (latestEvent.clientY - rect.top) + 'px');
      });
    }, { passive: true }, events.signal);
    return {
      destroy: () => {
        events.abort();
        if (frame) window.cancelAnimationFrame(frame);
      }
    };
  };

  const createRecommendationsController = (container) => {
    const requestController = new AbortController();
    let observer = null;
    let destroyed = false;

    const load = async () => {
      if (destroyed || container.dataset.loaded || !container.dataset.url) return;
      container.dataset.loaded = 'true';
      observer?.disconnect();
      try {
        const response = await fetch(container.dataset.url, {
          signal: requestController.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Recommendations could not be loaded.');
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = documentFragment.querySelector('[data-recommendations]');
        if (destroyed || !replacement?.innerHTML.trim()) return;
        container.innerHTML = replacement.innerHTML;
        initScope(container);
      } catch (error) {
        if (error.name !== 'AbortError' && !destroyed) container.hidden = true;
      }
    };

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) load();
      }, { rootMargin: '400px 0px' });
      observer.observe(container);
    } else {
      load();
    }

    return {
      destroy: () => {
        destroyed = true;
        observer?.disconnect();
        requestController.abort();
      }
    };
  };

  const createAutoSubmitController = (select) => {
    const events = new AbortController();
    listen(select, 'change', () => {
      if (!select.form) return;
      if (select.matches('[data-footer-country-selector]')) {
        const returnTo = select.form.querySelector('[name="return_to"]');
        if (returnTo) returnTo.value = window.location.pathname + window.location.search;
      }
      if (typeof select.form.requestSubmit === 'function') select.form.requestSubmit();
      else select.form.submit();
    }, false, events.signal);
    return { destroy: () => events.abort() };
  };

  const initializeMotionRegions = (scope) => {
    queryAll(scope, '[data-motion-region]').forEach((region) => {
      mountController(region, 'motion', createMotionController);
    });
    queryAll(scope, '[data-motion-toggle]').forEach((toggle) => {
      const region = resolveMotionRegion(toggle);
      if (region) mountController(region, 'motion', createMotionController);
    });
  };

  function initScope(scope = document) {
    queryAll(scope, '[data-cart-backdrop]').forEach((backdrop) => {
      mountController(backdrop, 'cart', createCartController);
    });
    queryAll(scope, '[data-mobile-menu]').forEach((menu) => {
      mountController(menu, 'menu', createMenuController);
    });
    queryAll(scope, '[data-hero-section]').forEach((hero) => {
      mountController(hero, 'hero', createHeroController);
    });
    initializeMotionRegions(scope);
    queryAll(scope, '[data-product-catalog]').forEach((catalog) => {
      mountController(catalog, 'catalog', createCatalogController);
    });
    queryAll(scope, '[data-collection-form]').forEach((form) => {
      mountController(form, 'facets', createFacetsController);
    });
    queryAll(scope, '[data-main-product]').forEach((product) => {
      mountController(product, 'product', createProductController);
    });
    queryAll(scope, '[data-reveal], .bundle-row, .story-section > *, .trust-strip article, .faq-list details, .newsletter > *')
      .forEach((node, index) => {
        mountController(node, 'reveal', (element) => createRevealController(element, index));
      });
    queryAll(scope, '[data-product-card]').forEach((card) => {
      mountController(card, 'card-glow', createCardGlowController);
    });
    queryAll(scope, '[data-recommendations]').forEach((container) => {
      mountController(container, 'recommendations', createRecommendationsController);
    });
    queryAll(scope, '[data-sort-select], [data-footer-country-selector]').forEach((select) => {
      mountController(select, 'auto-submit', createAutoSubmitController);
    });
  }

  const getCartController = (source) => {
    const backdrop = source?.closest?.('[data-cart-backdrop]') || document.querySelector('[data-cart-backdrop]');
    return backdrop ? cartInstances.get(backdrop) : null;
  };

  const getMenuController = (source) => {
    const menu = source?.closest?.('[data-mobile-menu]') || document.querySelector('[data-mobile-menu]');
    return menu ? menuInstances.get(menu) : null;
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('.js-product-form');
    if (!form) return;
    const cart = getCartController(form);
    if (!cart || !cart.shouldEnhanceForm(form, event.submitter)) return;
    event.preventDefault();
    cart.addFromForm(form, event.submitter);
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    if (!target) return;

    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen) {
      const cart = getCartController(cartOpen);
      if (cart) {
        event.preventDefault();
        cart.overlay.open(cartOpen);
      }
      return;
    }

    const cartClose = target.closest('[data-cart-close]');
    if (cartClose) {
      getCartController(cartClose)?.overlay.close();
      return;
    }

    const cartChange = target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      getCartController(cartChange)?.changeLine(cartChange);
      return;
    }

    const cartRemove = target.closest('[data-cart-remove]');
    if (cartRemove) {
      event.preventDefault();
      getCartController(cartRemove)?.changeLine(cartRemove);
      return;
    }

    const backdrop = target.closest('[data-cart-backdrop]');
    if (backdrop && target === backdrop) {
      cartInstances.get(backdrop)?.overlay.close();
      return;
    }

    const menuOpen = target.closest('[data-menu-open]');
    if (menuOpen) {
      const menu = getMenuController(menuOpen);
      if (menu) {
        event.preventDefault();
        menu.overlay.open(menuOpen);
      }
      return;
    }

    const menuClose = target.closest('[data-menu-close]');
    if (menuClose) {
      getMenuController(menuClose)?.overlay.close();
      return;
    }

    const menuLink = target.closest('[data-mobile-menu] a');
    if (menuLink) getMenuController(menuLink)?.overlay.close();
  });

  document.addEventListener('keydown', (event) => {
    overlayStack[overlayStack.length - 1]?.handleKeydown(event);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const heroSlide = target?.matches('[data-hero-slide]')
      ? target
      : target?.closest('[data-hero-slide]') || target?.querySelector('[data-hero-slide]');
    if (heroSlide) {
      const hero = heroSlide.closest('[data-hero-section]');
      const slideIndex = hero ? [...hero.querySelectorAll('[data-hero-slide]')].indexOf(heroSlide) : -1;
      if (hero && slideIndex !== -1) {
        mountController(hero, 'hero', createHeroController)?.selectSlide?.(slideIndex);
      }
      return;
    }
    const details = target?.matches('details')
      ? target
      : target?.closest('details') || target?.querySelector('details');
    if (!details) return;
    details.open = true;
    try {
      details.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    } catch (_) {
      details.scrollIntoView();
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    initScope(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target);
    syncBodyLock();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScope(document), { once: true });
  } else {
    initScope(document);
  }
})();

/* Floating candles: lit by default; hover softly extinguishes for 3 seconds. */
(() => {
  if (window.__mmFloatingCandles) return;
  window.__mmFloatingCandles = true;

  const states = new WeakMap();

  const candlesIn = (root) => {
    const items = [];

    if (root instanceof Element && root.matches('[data-floating-candle]')) {
      items.push(root);
    }

    root.querySelectorAll?.('[data-floating-candle]').forEach((item) => items.push(item));
    return items;
  };

  const mount = (root = document) => {
    candlesIn(root).forEach((candle) => {
      if (states.has(candle)) return;

      const controller = new AbortController();
      const state = { controller, fadeTimer: 0, relightTimer: 0 };

      const clearTimers = () => {
        window.clearTimeout(state.fadeTimer);
        window.clearTimeout(state.relightTimer);
        window.clearTimeout(state.igniteTimer);
        state.fadeTimer = 0;
        state.relightTimer = 0;
        state.igniteTimer = 0;
      };

      const relight = () => {
        candle.classList.remove('is-extinguishing');
        candle.classList.add('is-glowing', 'is-igniting');

        state.igniteTimer = window.setTimeout(() => {
          candle.classList.remove('is-igniting');
          state.igniteTimer = 0;
        }, 550);

        state.fadeTimer = 0;
        state.relightTimer = 0;
      };

      const extinguish = () => {
        if (
          candle.classList.contains('is-extinguishing')
          || candle.classList.contains('is-igniting')
          || !candle.classList.contains('is-glowing')
        ) return;

        clearTimers();
        candle.classList.add('is-extinguishing');

        state.fadeTimer = window.setTimeout(() => {
          candle.classList.remove('is-glowing', 'is-extinguishing');
          state.fadeTimer = 0;
        }, 650);

        state.relightTimer = window.setTimeout(relight, 3000);
      };

      candle.classList.add('is-glowing');
      candle.addEventListener('pointerenter', extinguish, {
        passive: true,
        signal: controller.signal
      });

      states.set(candle, state);
    });
  };

  const unmount = (root) => {
    candlesIn(root).forEach((candle) => {
      const state = states.get(candle);
      if (!state) return;

      window.clearTimeout(state.fadeTimer);
      window.clearTimeout(state.relightTimer);
      window.clearTimeout(state.igniteTimer);
      state.controller.abort();
      candle.classList.remove('is-extinguishing', 'is-igniting');
      candle.classList.add('is-glowing');
      states.delete(candle);
    });
  };

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  } else {
    mount(document);
  }
})();


/* DailySeasons: review sorting opens on click, not pointer hover. */
(() => {
  const triggerSelector = '.alr-wh-comp-review-sort';
  const menuSelector = '.alr-wh-comp-review-sort-wrap';
  let active = null, isOpen = false, bridge = false;
  document.documentElement.classList.add('ds-review-click-sort');
  const closest = (event, selector) => event.target instanceof Element ? event.target.closest(selector) : null;
  function signal(trigger, enter) {
    if (!trigger) return;
    bridge = true;
    try {
      trigger.dispatchEvent(new MouseEvent(enter ? 'mouseover' : 'mouseout', {bubbles:true,relatedTarget:document.body}));
      trigger.dispatchEvent(new MouseEvent(enter ? 'mouseenter' : 'mouseleave', {bubbles:false,relatedTarget:document.body}));
    } finally { bridge = false; }
  }
  function close(returnFocus = false) {
    const previous = active;
    isOpen = false;
    document.documentElement.classList.remove('ds-review-sort-open');
    signal(previous, false);
    if (previous) { previous.setAttribute('aria-expanded','false'); if (returnFocus) previous.focus(); }
    active = null;
  }
  function open(trigger) {
    close(); active = trigger; isOpen = true;
    document.documentElement.classList.add('ds-review-sort-open');
    signal(trigger, true);
    trigger.setAttribute('aria-expanded','true');
  }
  ['mouseover','mouseout','mouseenter','mouseleave','pointerover','pointerout','pointerenter','pointerleave'].forEach(type => {
    document.addEventListener(type, event => {
      if (!bridge && (closest(event,triggerSelector) || closest(event,menuSelector))) event.stopImmediatePropagation();
    }, true);
  });
  document.addEventListener('click', event => {
    const trigger = closest(event,triggerSelector);
    if (trigger) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (isOpen && active === trigger) close(); else open(trigger);
      return;
    }
    if (closest(event,'.alr-wh-comp-review-sort-item')) { setTimeout(() => close(),0); return; }
    if (isOpen && !closest(event,menuSelector)) close();
  },true);
  document.addEventListener('keydown',event => {
    const trigger = closest(event,triggerSelector);
    if (trigger && ['Enter',' ','ArrowDown'].includes(event.key)) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (isOpen && event.key !== 'ArrowDown') close(true); else open(trigger);
      if (event.key === 'ArrowDown') requestAnimationFrame(() => document.querySelector(menuSelector+' [role="option"]')?.focus());
    } else if (isOpen && event.key === 'Escape') {
      event.preventDefault(); event.stopImmediatePropagation(); close(true);
    }
  },true);
  window.addEventListener('resize',() => { if(isOpen) close(); },{passive:true});
})();


/* DailySeasons: photo-only lightbox for customer-review images. */
(() => {
  const thumbSelector = '.alireviews-review-box .alr-wh-comp-review-image[role="button"]';
  let dialog, image, counter, status, previous, next, opener, photos = [], index = 0, touchStart = null;
  const icon = path => '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="'+path+'"/></svg>';
  function sourceFor(thumb) {
    const picture = thumb.querySelector('img');
    if (!picture) return null;
    const candidates = [picture.currentSrc, picture.getAttribute('src'), /^https?:\/\//.test(picture.alt) ? picture.alt : null];
    for (const source of candidates) {
      if (!source) continue;
      try { const url = new URL(source,location.href); if (/^https?:$/.test(url.protocol)) return url.href; } catch(e) {}
    }
    return null;
  }
  function close() { if(dialog?.open) dialog.close(); }
  function render() {
    if (!photos.length) return;
    dialog.classList.add('is-loading'); dialog.classList.remove('has-error');
    status.textContent = 'Loading photo…';
    counter.textContent = (index+1)+' / '+photos.length;
    image.alt = 'Customer review photo '+(index+1)+' of '+photos.length;
    image.src = photos[index].src;
    previous.hidden = next.hidden = photos.length < 2;
    if (image.complete && image.naturalWidth) loaded();
  }
  function loaded() { dialog.classList.remove('is-loading'); status.textContent = ''; }
  function step(amount) { index = (index+amount+photos.length)%photos.length; render(); }
  function ensureDialog() {
    if (dialog) return;
    dialog = document.createElement('dialog'); dialog.className = 'ds-review-lightbox'; dialog.id = 'DSReviewPhotos';
    dialog.setAttribute('aria-label','Customer review photos');
    dialog.innerHTML = '<figure class="ds-review-lightbox__figure"><button type="button" class="ds-review-lightbox__button ds-review-lightbox__close" aria-label="Close review photos">'+icon('M6 6l12 12M18 6 6 18')+'</button><img class="ds-review-lightbox__image" alt="" draggable="false"><figcaption class="ds-review-lightbox__count" aria-live="polite" aria-atomic="true"></figcaption><p class="ds-review-lightbox__status" role="status"></p></figure><button type="button" class="ds-review-lightbox__button ds-review-lightbox__previous" aria-label="Previous review photo">'+icon('m15 5-7 7 7 7')+'</button><button type="button" class="ds-review-lightbox__button ds-review-lightbox__next" aria-label="Next review photo">'+icon('m9 5 7 7-7 7')+'</button>';
    document.body.appendChild(dialog);
    image = dialog.querySelector('img'); counter = dialog.querySelector('figcaption'); status = dialog.querySelector('[role="status"]');
    previous = dialog.querySelector('.ds-review-lightbox__previous'); next = dialog.querySelector('.ds-review-lightbox__next');
    dialog.querySelector('.ds-review-lightbox__close').addEventListener('click',close);
    previous.addEventListener('click',()=>step(-1)); next.addEventListener('click',()=>step(1));
    image.addEventListener('load',loaded);
    image.addEventListener('error',()=>{ dialog.classList.remove('is-loading'); dialog.classList.add('has-error'); status.textContent = 'This photo could not load. Try another photo.'; });
    dialog.addEventListener('click',event=>{ if(event.target === dialog) close(); });
    dialog.addEventListener('cancel',event=>{ event.preventDefault(); close(); });
    dialog.addEventListener('close',()=>{document.documentElement.classList.remove('ds-review-photo-open');if(opener?.isConnected)opener.focus({preventScroll:true});});
    dialog.addEventListener('keydown',event=>{
      if(event.key==='ArrowLeft'||event.key==='ArrowRight'){event.preventDefault();event.stopPropagation();step(event.key==='ArrowLeft'?-1:1);}
    });
    dialog.addEventListener('touchstart',event=>{touchStart=event.touches.length===1?{x:event.touches[0].clientX,y:event.touches[0].clientY}:null;},{passive:true});
    dialog.addEventListener('touchend',event=>{if(!touchStart||!event.changedTouches.length)return;const dx=event.changedTouches[0].clientX-touchStart.x,dy=event.changedTouches[0].clientY-touchStart.y;touchStart=null;if(Math.abs(dx)>45&&Math.abs(dx)>Math.abs(dy)*1.4)step(dx<0?1:-1);},{passive:true});
    dialog.addEventListener('touchcancel',()=>{touchStart=null;},{passive:true});
  }
  function open(thumb,event) {
    const box = thumb.closest('.alireviews-review-box');
    const visiblePhotos = [...box.querySelectorAll('.alr-wh-comp-review-image[role="button"]')].filter(item=>item.getClientRects().length).map(item=>({thumb:item,src:sourceFor(item)})).filter(item=>item.src);
    const selected = visiblePhotos.findIndex(item=>item.thumb===thumb);
    if(selected<0)return;
    ensureDialog();
    event.preventDefault();event.stopImmediatePropagation();
    photos=visiblePhotos;index=selected;opener=thumb;
    document.documentElement.classList.add('ds-review-photo-open');
    render();dialog.showModal();dialog.querySelector('.ds-review-lightbox__close').focus();
  }
  document.addEventListener('click',event=>{const thumb=event.target instanceof Element?event.target.closest(thumbSelector):null;if(thumb)open(thumb,event);},true);
  document.addEventListener('keydown',event=>{if(!['Enter',' '].includes(event.key))return;const thumb=event.target instanceof Element?event.target.closest(thumbSelector):null;if(thumb)open(thumb,event);},true);
})();
